import React, { useState, useMemo, useEffect } from 'react';
import { ConfigTemplateCreate, DeviceType, JsonConfig, getPriorityLevel } from '../../types/configTemplate';
import { apiClient } from '../../services/apiClient';
import { clientService } from '../../services/clientService';
import { DeviceTypeSelector } from '../common/DeviceTypeSelector';
import { typesService, HardwareFieldDefinition } from '../../services/typesService';
import { useI18n } from '../../contexts/I18nContext';
import { TemplateImpactPreviewModal } from './TemplateImpactPreviewModal';
import { ClientTargetingInput, DEFAULT_CLIENT_ID } from './ClientTargetingInput';
import { getTypeBadgeColor } from '../../utils/fieldTypes';
import { Client } from '../../types/client';

const DEFAULT_GROUPING_NAME = 'General';

interface CreateConfigTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTemplateCreated: (template: ConfigTemplateCreate) => void;
}

interface FormErrors {
  name?: string;
  device_type?: string;
  priority?: string;
  password?: string;
  // Schema-driven field errors use the snake_case field_name as the key,
  // so FormErrors is effectively an open bag of field_name → message.
  [key: string]: string | undefined;
}

export const CreateConfigTemplateModal: React.FC<CreateConfigTemplateModalProps> = ({
  isOpen,
  onClose,
  onTemplateCreated
}) => {
  const { t } = useI18n();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPreview, setShowPreview] = useState(false);

  // Get axios instance from apiClient
  const axiosInstance = useMemo(() => apiClient.getAxiosInstance(), []);

  // Clients state. targetingIds is the wire-format array sent to the backend:
  // [DEFAULT_CLIENT_ID] means global (applies to every device of this
  // deviceType), any other non-empty array means the template applies only
  // to devices whose idClient is in the list. The ClientTargetingInput
  // combobox is the single control that produces this value.
  const [clients, setClients] = useState<Client[]>([]);
  const [targetingIds, setTargetingIds] = useState<string[]>([DEFAULT_CLIENT_ID]);

  // Form state - basic info
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [deviceType, setDeviceType] = useState<DeviceType>('');
  const [priority, setPriority] = useState(50);

  // Hardware-type schema drives which fields the form displays. null means
  // either no type selected yet, or fetch in-flight.
  const [schema, setSchema] = useState<HardwareFieldDefinition[] | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  // Values for every field in the schema, keyed by field_name (snake_case).
  // At submit time this dict becomes the template's json_config body.
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  // Device password — template-level setting, independent of the hardware
  // schema. When generatePassword is true, the backend generates a random
  // password at device creation time using this template.
  const [password, setPassword] = useState('');
  const [generatePassword, setGeneratePassword] = useState(false);

  // Fetch clients scoped to the selected deviceType. Until the user picks a
  // device type we have nothing to fetch — the targeting combobox stays
  // disabled and the list is empty. When deviceType changes we drop any
  // specific selections that aren't valid for the new list (they'd have no
  // effect at save time since the backend's Path-1 scope match is keyed on
  // deviceType + client_id).
  const [clientsLoading, setClientsLoading] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    if (!deviceType) {
      setClients([]);
      return;
    }
    let cancelled = false;
    setClientsLoading(true);
    clientService
      .fetchClientsByDeviceType(deviceType)
      .then((list) => {
        if (cancelled) return;
        setClients(list);
        // Prune stale selections that no longer belong to this deviceType's
        // client set — keep DEFAULT_CLIENT_ID (global) untouched.
        setTargetingIds((prev) => {
          if (prev.includes(DEFAULT_CLIENT_ID)) return prev;
          const validIds = new Set(list.map((c) => c.id));
          const next = prev.filter((id) => validIds.has(id));
          return next.length === prev.length ? prev : next;
        });
      })
      .catch((err) => {
        if (!cancelled) console.error('[CREATE CONFIG TEMPLATE] fetch clients:', err);
      })
      .finally(() => {
        if (!cancelled) setClientsLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, deviceType]);

  // Load the hardware type's field-definition schema whenever deviceType
  // changes. The schema drives which fields are shown and validated —
  // only fields defined on this hardware type appear in the form.
  useEffect(() => {
    if (!deviceType) {
      setSchema(null);
      setFieldValues({});
      return;
    }
    setSchemaLoading(true);
    // Look up the hardware type id by code so we can hit the field-defs
    // endpoint. getHardwareTypes is cached on the service.
    typesService
      .getHardwareTypes(true)
      .then((types) => {
        const hw = types.find((t) => t.code === deviceType);
        if (!hw) {
          setSchema([]);
          return null;
        }
        return typesService.getHardwareFieldDefinitions(hw.id);
      })
      .then((response) => {
        if (!response) return;
        const sorted = [...response.items].sort((a, b) => {
          const groupCmp = (a.grouping_name || DEFAULT_GROUPING_NAME).localeCompare(
            b.grouping_name || DEFAULT_GROUPING_NAME,
          );
          if (groupCmp !== 0) return groupCmp;
          return (a.display_order || 0) - (b.display_order || 0);
        });
        setSchema(sorted);
        // Pre-fill defaults — keep values the user already typed.
        setFieldValues((prev) => {
          const next = { ...prev };
          for (const def of sorted) {
            if (
              next[def.field_name] === undefined &&
              def.default_value !== undefined &&
              def.default_value !== null &&
              def.default_value !== ''
            ) {
              next[def.field_name] = String(def.default_value);
            }
          }
          return next;
        });
      })
      .catch((err) => {
        console.error('[CREATE CONFIG TEMPLATE] Failed to fetch schema:', err);
        setSchema([]);
      })
      .finally(() => setSchemaLoading(false));
  }, [deviceType]);

  const setFieldValue = (fieldName: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [fieldName]: value }));
    if (errors[fieldName]) {
      setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
    }
  };

  const parseOptions = (opt: unknown): string[] => {
    if (!opt) return [];
    if (Array.isArray(opt)) return opt.map(String);
    if (typeof opt === 'string') {
      try {
        const parsed = JSON.parse(opt);
        if (Array.isArray(parsed)) return parsed.map(String);
      } catch {
        /* noop */
      }
    }
    return [];
  };

  const renderFieldRow = (def: HardwareFieldDefinition): React.ReactNode => {
    const value = fieldValues[def.field_name] ?? '';
    const error = errors[def.field_name];
    const placeholder = def.placeholder || '—';

    let control: React.ReactNode;
    switch (def.value_type) {
      case 'boolean':
        control = (
          <label className="cell-checkbox" style={{ justifyContent: 'flex-start', gap: '0.5rem' }}>
            <input
              type="checkbox"
              disabled={isLoading}
              checked={value === 'true'}
              onChange={(e) => setFieldValue(def.field_name, e.target.checked ? 'true' : 'false')}
            />
            <span style={{ fontSize: '0.8rem', color: 'var(--theme-text-secondary, #6b7280)' }}>
              {value === 'true' ? 'Yes' : 'No'}
            </span>
          </label>
        );
        break;
      case 'password':
        control = <input type="password" aria-label={def.field_name} className="cell-input mono" value={value} placeholder={placeholder} disabled={isLoading} onChange={(e) => setFieldValue(def.field_name, e.target.value)} />;
        break;
      case 'port':
      case 'number':
        control = <input type="number" aria-label={def.field_name} className="cell-input mono" value={value} placeholder={placeholder} disabled={isLoading} min={def.min_value ?? undefined} max={def.max_value ?? undefined} onChange={(e) => setFieldValue(def.field_name, e.target.value)} />;
        break;
      case 'dropdown':
        control = (
          <select aria-label={def.field_name} className="cell-select" value={value} disabled={isLoading} onChange={(e) => setFieldValue(def.field_name, e.target.value)}>
            <option value="">{placeholder}</option>
            {parseOptions(def.options).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        );
        break;
      default:
        control = <input type="text" aria-label={def.field_name} className="cell-input mono" value={value} placeholder={placeholder} disabled={isLoading} onChange={(e) => setFieldValue(def.field_name, e.target.value)} />;
    }

    return (
      <tr key={def.id} className={error ? 'row-invalid' : ''}>
        <td><code>{def.field_name}</code></td>
        <td><span className="type-badge" style={{ backgroundColor: getTypeBadgeColor(def.value_type) }}>{def.value_type}</span></td>
        <td>{control}{error && <div className="field-error" style={{ fontSize: '0.7rem', marginTop: 2 }}>{error}</div>}</td>
      </tr>
    );
  };

  // Hardware types may define a `password` field. When they do, the
  // Device Password section below handles it (so the user gets the
  // "generate random per device" toggle); we then skip it in the main
  // schema renderer to avoid duplicating the input.
  const hasPasswordField = useMemo(
    () => !!schema?.some((def) => def.field_name === 'password'),
    [schema],
  );

  const groupedSchema: Array<{ name: string; fields: HardwareFieldDefinition[] }> = useMemo(() => {
    if (!schema) return [];
    const byGroup = new Map<string, HardwareFieldDefinition[]>();
    for (const def of schema) {
      // Skip the password field — rendered by the dedicated Device
      // Password section with the random-generation toggle.
      if (def.field_name === 'password') continue;
      const g = def.grouping_name || DEFAULT_GROUPING_NAME;
      if (!byGroup.has(g)) byGroup.set(g, []);
      byGroup.get(g)!.push(def);
    }
    return Array.from(byGroup.entries())
      .map(([name, fields]) => ({ name, fields }))
      .filter((g) => g.fields.length > 0);
  }, [schema]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Always-required identity fields
    if (!name.trim()) {
      newErrors.name = 'Template name is required';
    } else if (name.trim().length < 2) {
      newErrors.name = 'Template name must be at least 2 characters';
    } else if (name.trim().length > 100) {
      newErrors.name = 'Template name must not exceed 100 characters';
    }

    if (!deviceType) {
      newErrors.device_type = 'Device type is required';
    }

    if (priority < 1 || priority > 100) {
      newErrors.priority = 'Priority must be between 1 and 100';
    }

    if (targetingIds.length === 0) {
      newErrors.client_ids = 'Select at least one client, or "All clients"';
    }

    // Template context: a template is a PARTIAL overlay that merges over
    // the hardware-type defaults and other templates. Empty fields mean
    // "do not override" — they are not missing data. So we intentionally
    // do NOT enforce schema `is_required` here. We still validate the
    // format of anything the user DID type so the backend doesn't reject
    // malformed values on merge.
    if (schema) {
      for (const def of schema) {
        const raw = (fieldValues[def.field_name] ?? '').toString();
        if (!raw.trim()) continue; // leave untouched — no error
        if (def.value_type === 'port' || def.field_name.endsWith('_port')) {
          const n = Number(raw);
          if (isNaN(n) || n < 1 || n > 65535) {
            newErrors[def.field_name] = `${def.display_name} must be between 1 and 65535`;
          }
        }
        if (def.value_type === 'number') {
          const n = Number(raw);
          if (isNaN(n)) {
            newErrors[def.field_name] = `${def.display_name} must be a number`;
          } else {
            if (def.min_value != null && n < def.min_value) {
              newErrors[def.field_name] = `${def.display_name} minimum is ${def.min_value}`;
            }
            if (def.max_value != null && n > def.max_value) {
              newErrors[def.field_name] = `${def.display_name} maximum is ${def.max_value}`;
            }
          }
        }
      }
    }

    // Device password is also optional in template context. Leaving it
    // blank means the template doesn't override the password on devices
    // it's applied to. The `generatePassword` toggle is a separate
    // instruction that stands on its own.
    // (no password-required validation)

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Build the full payload the backend expects.
   *
   * Extracted so both the "Save" submit handler and the "Preview
   * impact" flow serialize the form identically — otherwise the
   * preview and the real save could drift apart.
   */
  const buildTemplatePayload = (): ConfigTemplateCreate => {
    const jsonConfig: JsonConfig = {};
    if (schema) {
      for (const def of schema) {
        if (def.field_name === 'password') continue;
        const raw = fieldValues[def.field_name];
        if (raw === undefined || raw === null || raw === '') continue;
        if (def.value_type === 'port' || def.value_type === 'number') {
          const n = Number(raw);
          (jsonConfig as Record<string, unknown>)[def.field_name] = Number.isNaN(n) ? raw : n;
        } else if (def.value_type === 'boolean') {
          (jsonConfig as Record<string, unknown>)[def.field_name] = raw === 'true';
        } else {
          (jsonConfig as Record<string, unknown>)[def.field_name] = raw;
        }
      }
    }
    if (hasPasswordField) {
      jsonConfig.generate_password = generatePassword;
      if (!generatePassword && password.trim()) {
        jsonConfig.password = password.trim();
      }
    }
    return {
      name: name.trim(),
      description: description.trim() || undefined,
      client_id: targetingIds[0], // back-compat: first in list
      client_ids: targetingIds,
      device_type: deviceType,
      priority: priority,
      json_config: jsonConfig,
    };
  };

  /**
   * Persist the template. Shared by the normal Save path and the
   * "confirm" button inside the preview modal.
   */
  const persistTemplate = async () => {
    const templateData = buildTemplatePayload();
    await axiosInstance.post('/v2/config-templates', templateData);
    onTemplateCreated(templateData);
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      await persistTemplate();
    } catch (err: any) {
      console.error('[CREATE CONFIG TEMPLATE] Error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create configuration template';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = () => {
    setError(null);
    if (!validateForm()) return;
    setShowPreview(true);
  };

  const handleConfirmFromPreview = async () => {
    setError(null);
    setIsLoading(true);
    try {
      await persistTemplate();
      setShowPreview(false);
    } catch (err: any) {
      console.error('[CREATE CONFIG TEMPLATE] Error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create configuration template';
      setError(errorMessage);
      // Keep preview open so the user can see the error context.
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setTargetingIds([DEFAULT_CLIENT_ID]);
    setDeviceType('');
    setPriority(50);
    setPassword('');
    setGeneratePassword(false);
    setSchema(null);
    setFieldValues({});
    setErrors({});
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getPriorityColor = () => {
    return getPriorityLevel(priority).color;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="config-template-modal"
        data-testid="modal-create-config-template"
        onClick={e => e.stopPropagation()}
        style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
      >
        <div className="modal-header">
          <h2>{t('configTemplates.createTitle')}</h2>
          <button className="close-button" data-testid="btn-modal-close" onClick={handleClose} aria-label={t('common.close')}>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div className="modal-content" style={{ overflowY: 'auto', flex: 1 }}>
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="form-section">
              <h3>{t('devices.basicInformation')}</h3>

              <div className="form-group">
                <label htmlFor="name">{t('configTemplates.templateName')} *</label>
                <input
                  type="text"
                  id="name"
                  data-testid="field-template-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('configTemplates.namePlaceholder')}
                  className={errors.name ? 'error' : ''}
                />
                {errors.name && <span className="field-error">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="description">{t('common.description')}</label>
                <textarea
                  id="description"
                  data-testid="field-template-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('configTemplates.descriptionPlaceholder')}
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="deviceType">{t('devices.deviceType')} *</label>
                  <DeviceTypeSelector
                    id="deviceType"
                    value={deviceType}
                    onChange={(value) => {
                      setDeviceType(value);
                      // The schema-loading useEffect clears fieldValues
                      // and reloads defaults when deviceType changes.
                    }}
                    placeholder={t('configTemplates.selectHardwareTypePlaceholder')}
                    includeEmpty={true}
                    className={errors.device_type ? 'error' : ''}
                  />
                  {errors.device_type && <span className="field-error">{errors.device_type}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="clientTargeting">
                    {t('configTemplates.targetingLabel')} *
                  </label>
                  <ClientTargetingInput
                    id="clientTargeting"
                    value={targetingIds}
                    onChange={(next) => {
                      setTargetingIds(next);
                      if (errors.client_ids) {
                        setErrors((prev) => ({ ...prev, client_ids: undefined }));
                      }
                    }}
                    clients={clients}
                    error={!!errors.client_ids}
                    disabled={!deviceType || clientsLoading}
                    disabledHint={
                      !deviceType
                        ? t('configTemplates.targetingPickDeviceTypeFirst')
                        : t('configTemplates.targetingLoadingClients')
                    }
                  />
                  {errors.client_ids && (
                    <span className="field-error">{errors.client_ids}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="priority">
                  Priority * (1-100)
                  <span
                    style={{
                      marginLeft: '8px',
                      padding: '2px 8px',
                      backgroundColor: getPriorityColor(),
                      color: '#fff',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  >
                    {getPriorityLevel(priority).label}
                  </span>
                </label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="number"
                    id="priority"
                    data-testid="field-template-priority"
                    min="1"
                    max="100"
                    value={priority}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val) && val >= 1 && val <= 100) {
                        setPriority(val);
                      }
                    }}
                    style={{ width: '70px', textAlign: 'center' }}
                    className={errors.priority ? 'error' : ''}
                  />
                  <select
                    value={[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].includes(priority) ? priority : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        setPriority(parseInt(e.target.value, 10));
                      }
                    }}
                    style={{ flex: 1 }}
                  >
                    <option value="">Quick select...</option>
                    <option value="10">10 - Very Low</option>
                    <option value="20">20 - Low</option>
                    <option value="30">30 - Below Normal</option>
                    <option value="40">40 - Normal Low</option>
                    <option value="50">50 - Normal</option>
                    <option value="60">60 - Normal High</option>
                    <option value="70">70 - Above Normal</option>
                    <option value="80">80 - High</option>
                    <option value="90">90 - Very High</option>
                    <option value="100">100 - Critical</option>
                  </select>
                </div>
                {errors.priority && <span className="field-error">{errors.priority}</span>}
                <small
                  style={{
                    display: 'block',
                    marginTop: 4,
                    color: '#5f6368',
                    fontSize: 12,
                    lineHeight: 1.4,
                  }}
                >
                  Templates are merged in ascending order (1 → 100). A template with
                  priority 100 overrides conflicting values from lower-priority
                  templates. Devices see the result of all matching templates, not
                  just one.
                </small>
              </div>
            </div>

            {/* Schema-driven sections — only visible once a device type is picked */}
            {!deviceType ? (
              <div className="form-section">
                <p style={{ color: '#6b7280', fontSize: 14, padding: 12, textAlign: 'center' }}>
                  Select a device type above to load its configuration fields.
                </p>
              </div>
            ) : schemaLoading ? (
              <div className="form-section">
                <p style={{ color: '#6b7280', fontSize: 14, padding: 12, textAlign: 'center' }}>
                  Loading schema for {deviceType}…
                </p>
              </div>
            ) : schema && schema.length === 0 ? (
              <div className="form-section">
                <p style={{ color: '#6b7280', fontSize: 14, padding: 12 }}>
                  No field definitions are configured for hardware type{' '}
                  <strong>{deviceType}</strong>. Define fields on this hardware
                  type first (Device Types → Field Definitions), then create
                  the template so values can be applied via the assembly pipeline.
                </p>
              </div>
            ) : (
              <>
                <div
                  style={{
                    padding: 12,
                    marginBottom: 16,
                    background: '#e3f2fd',
                    border: '1px solid #90caf9',
                    borderRadius: 6,
                    color: '#0d47a1',
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  Templates are partial overlays. <strong>Leave a field blank to
                  not override it</strong> — devices will fall back to the
                  hardware default, a lower-priority template, or an existing
                  device value. Fill only the fields you want this template to
                  set.
                </div>

                <div className="field-defs-list">
                  {groupedSchema.map((group) => (
                    <div className="field-group" key={group.name}>
                      <h4 className="group-header">{group.name}</h4>
                      <table className="field-defs-table">
                        <thead>
                          <tr>
                            <th>{t('devices.fieldName')}</th>
                            <th>Type</th>
                            <th>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.fields.map((def) => renderFieldRow(def))}
                        </tbody>
                      </table>
                    </div>
                  ))}

                  {hasPasswordField && (
                    <div className="field-group">
                      <h4 className="group-header">{t('configTemplates.devicePassword')}</h4>
                      <table className="field-defs-table">
                        <thead><tr><th>{t('devices.fieldName')}</th><th>{t('devices.typeLabel')}</th><th>{t('devices.value')}</th></tr></thead>
                        <tbody>
                          <tr>
                            <td><code>generate_password</code></td>
                            <td><span className="type-badge" style={{ backgroundColor: getTypeBadgeColor('boolean') }}>boolean</span></td>
                            <td>
                              <label className="cell-checkbox" style={{ justifyContent: 'flex-start', gap: '0.5rem' }}>
                                <input
                                  type="checkbox"
                                  checked={generatePassword}
                                  onChange={(e) => {
                                    setGeneratePassword(e.target.checked);
                                    if (e.target.checked) {
                                      setPassword('');
                                      setErrors((prev) => ({ ...prev, password: undefined }));
                                    }
                                  }}
                                />
                                <span style={{ fontSize: '0.8rem', color: 'var(--theme-text-secondary, #6b7280)' }}>
                                  {generatePassword ? 'Random per device' : 'No'}
                                </span>
                              </label>
                            </td>
                          </tr>
                          {!generatePassword && (
                            <tr>
                              <td><code>password</code></td>
                              <td><span className="type-badge" style={{ backgroundColor: getTypeBadgeColor('password') }}>password</span></td>
                              <td>
                                <input type="text" className="cell-input mono" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('configTemplates.leaveBlankToNotOverride')} maxLength={100} />
                                {errors.password && <div className="field-error" style={{ fontSize: '0.7rem', marginTop: 2 }}>{errors.password}</div>}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              data-testid="btn-cancel"
              onClick={handleClose}
              disabled={isLoading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              data-testid="btn-preview-impact"
              onClick={handlePreview}
              disabled={isLoading}
              title={t('templateImpact.subtitle')}
            >
              {t('templateImpact.previewButton')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              data-testid="btn-submit"
              disabled={isLoading}
            >
              {isLoading ? t('common.save') + '…' : t('common.create')}
            </button>
          </div>
        </form>
      </div>

      <TemplateImpactPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        onConfirm={handleConfirmFromPreview}
        confirming={isLoading}
        request={
          showPreview
            ? {
                template_id: null,
                name: name.trim() || undefined,
                device_type: deviceType,
                priority: priority,
                json_config: buildTemplatePayload().json_config as Record<string, unknown>,
                client_ids: targetingIds,
              }
            : null
        }
      />
    </div>
  );
};
